import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, CreditCard, Sparkles, Gift, Award } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useSettings } from "@/context/SettingsContext";
import { useCategories } from "@/context/CategoriesContext";

function SectionHeader({ title, subtitle, to }) {
  return (
    <div className="flex items-end justify-between mb-7 gap-4 border-b-2 border-[var(--mint-900)]/15 pb-4">
      <div>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-[var(--mint-900)]">{title}</h2>
        {subtitle && <p className="text-mint-800/70 text-sm sm:text-base mt-1.5 max-w-2xl">{subtitle}</p>}
      </div>
      {to && <Link to={to} className="hidden sm:flex items-center gap-1.5 text-[var(--mint-600)] font-bold text-xs hover:gap-2.5 transition-all shrink-0 uppercase tracking-[0.18em]">Voir tout <ArrowRight className="w-4 h-4" /></Link>}
    </div>
  );
}

function Grid({ products }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
      {products.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const { categories } = useCategories();
  const [promos, setPromos] = useState([]);
  const [news, setNews] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [brands, setBrands] = useState([]);
  const [blog, setBlog] = useState([]);

  useEffect(() => {
    api.get("/products?on_promo=1&limit=8").then((r) => setPromos(r.data));
    api.get("/products?is_new=1&limit=8").then((r) => setNews(r.data));
    api.get("/products?featured=1&limit=8").then((r) => setFeatured(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/blog").then((r) => setBlog(r.data.slice(0, 3)));
  }, []);

  return (
    <div className="animate-fade-up">
      {/* HERO — apothicaire éditorial : asymétrique, carré, ombres dures */}
      <section className="relative overflow-hidden border-b-2 border-[var(--mint-900)]" style={{ background: "var(--background)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16 sm:pt-10 sm:pb-20 grid lg:grid-cols-12 gap-x-12 gap-y-10 items-start">
          <div className="lg:col-span-7 relative z-10 order-1">
            <p className="font-script text-2xl sm:text-3xl text-[var(--brand-accent)] mb-3 -rotate-2 inline-block">{settings.hero_badge || "Bienvenue à l'herboristerie moderne"}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.98] text-[var(--mint-900)]">
              {settings.hero_title || "Prenez soin de votre peau & santé au meilleur prix"}
            </h1>
            <p className="text-mint-800/70 text-base sm:text-lg mt-6 max-w-xl">{settings.hero_subtitle}</p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/catalogue?on_promo=1" data-testid="hero-cta-promos"
                className="px-8 py-4 bg-[var(--mint-600)] text-[var(--cream)] font-bold uppercase tracking-[0.15em] text-sm border-2 border-[var(--mint-600)] hover:shadow-[6px_6px_0_var(--brand-accent)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all">
                Découvrir les promos
              </Link>
              <Link to="/catalogue"
                className="px-8 py-4 border-2 border-[var(--mint-900)] text-[var(--mint-900)] font-bold uppercase tracking-[0.15em] text-sm hover:bg-[var(--mint-900)] hover:text-[var(--cream)] transition-all">
                Tout le catalogue
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative order-2 lg:row-span-2">
            <div className="border-2 border-[var(--mint-900)] shadow-[12px_12px_0_var(--brand-accent)] bg-white">
              <img src={settings.hero_image ? mediaUrl(settings.hero_image) : "https://images.unsplash.com/photo-1728727267814-792db55ce678?w=900"} alt="Soin visage L'olivier" className="w-full aspect-[16/10] sm:aspect-[4/3] lg:aspect-[4/5] object-cover object-[32%_center]" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-[var(--cream)] border-2 border-[var(--mint-900)] px-5 py-3 shadow-[6px_6px_0_var(--mint-900)]">
              <span className="font-script text-xl text-[var(--brand-accent)]">jusqu'à</span>
              <div className="font-display text-3xl font-semibold text-[var(--mint-900)] leading-none">-50%</div>
            </div>
          </div>
          <div className="lg:col-span-7 relative z-10 order-3">
            <div className="flex flex-wrap gap-8 pt-7 border-t-2 border-[var(--mint-900)]/10">
              {[["250+", "Références certifiées"], ["24/7", "Conseil pharmacien"], ["58", "Wilayas livrées"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-display text-3xl sm:text-4xl font-semibold text-[var(--mint-600)]">{v}</p>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-mint-800/70 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE ÉDITORIAL */}
      <div className="overflow-hidden bg-[var(--mint-900)] py-3.5" data-testid="home-marquee">
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex shrink-0 items-center" aria-hidden={dup === 1}>
              {["Conseil pharmacien 7j/7 — 24h/24", "Livraison dans les 58 wilayas", "Paiement à la livraison", "Programme Privilège Bronze · Silver · Gold", "Produits 100% certifiés"].map((t) => (
                <span key={`${dup}-${t}`} className="flex items-center">
                  <span className="whitespace-nowrap px-6 font-script text-xl text-[var(--cream)]/90">{t}</span>
                  <span className="text-[var(--brand-accent)] text-xs">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Bannière fidélité — juste après le hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16" data-testid="banner-loyalty-top">
        <Link to="/fidelite" className="group block border-2 border-[var(--mint-900)] bg-[var(--brand-accent)] text-[var(--cream)] p-8 sm:p-12 hover:shadow-[8px_8px_0_var(--mint-900)] transition-all">
          <span className="font-script text-2xl sm:text-3xl">programme privilège</span>
          <h3 className="font-display italic font-medium text-3xl sm:text-5xl leading-tight mt-2">Découvrez notre programme de fidélité</h3>
          <p className="text-[var(--cream)]/80 mt-3 max-w-2xl">Gagnez des points à chaque commande, montez en statut Bronze, Silver et Gold, et échangez vos points contre des récompenses exclusives.</p>
          <span className="mt-5 inline-flex items-center gap-2 font-bold uppercase tracking-[0.18em] text-xs underline underline-offset-4 group-hover:gap-3 transition-all">Découvrir le programme <ArrowRight className="w-4 h-4" /></span>
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-14 sm:space-y-20 pb-12 sm:pb-16 pt-10">
        {promos.length > 0 && (
          <section data-testid="section-offres">
            <SectionHeader title="🔥 Nos Offres" subtitle="Réductions exclusives avec prix barrés en Dinar Algérien" to="/catalogue?on_promo=1" />
            <Grid products={promos} />
          </section>
        )}

        {news.length > 0 && (
          <section data-testid="section-nouveautes">
            <SectionHeader title="✨ Nouvel Arrivage" subtitle="Les derniers soins et innovations beauté chez L'olivier" to="/catalogue?is_new=1" />
            <Grid products={news} />
          </section>
        )}

        {/* Categories — mosaïque éditoriale numérotée */}
        <section data-testid="section-categories">
          <SectionHeader title="Des catégories pour chaque besoin" subtitle="Trouvez le soin adapté à vos besoins spécifiques" />
          <div className="grid grid-cols-2 sm:grid-cols-4 auto-rows-[150px] sm:auto-rows-[170px] gap-4">
            {categories.map((c, i) => (
              <Link key={c.id} to={`/categorie/${c.id}`} data-testid={`home-category-${c.id}`}
                className={`group relative overflow-hidden border-2 border-[var(--mint-900)] bg-[var(--mint-50)] hover:shadow-[6px_6px_0_var(--mint-900)] transition-all ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
                <img src={mediaUrl(c.image)} alt={c.label} className="w-full h-full object-cover grayscale-[25%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--mint-900)]/75 via-[var(--mint-900)]/10 to-transparent" />
                <span className="absolute top-2 left-3 font-script text-2xl text-[var(--cream)] drop-shadow">0{i + 1}</span>
                <span className="absolute bottom-3 left-3 right-3 font-display font-semibold text-[var(--cream)] text-base sm:text-lg leading-tight">{c.label}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Bannière carte cadeau */}
        <section data-testid="section-promo-banners">
          <Link to="/carte-cadeau" data-testid="banner-giftcard" className="group block border-2 border-[var(--mint-900)] bg-[var(--mint-900)] text-[var(--cream)] p-8 sm:p-12 hover:shadow-[8px_8px_0_var(--brand-accent)] transition-all">
            <span className="font-script text-2xl sm:text-3xl text-[var(--brand-accent)]">cartes cadeaux</span>
            <h3 className="font-display italic font-medium text-3xl sm:text-5xl leading-tight mt-2">Offrez une carte cadeau L'olivier</h3>
            <p className="text-[var(--cream)]/70 mt-3 max-w-2xl">Le cadeau idéal pour vos proches : montant au choix, livraison partout en Algérie.</p>
            <span className="mt-5 inline-flex items-center gap-2 font-bold uppercase tracking-[0.18em] text-xs underline underline-offset-4 group-hover:gap-3 transition-all">Commander une carte <ArrowRight className="w-4 h-4" /></span>
          </Link>
        </section>

          <section data-testid="section-coups-de-coeur">
            <SectionHeader title="💚 Nos Coups de Cœur" subtitle="Sélectionnés et approuvés par nos pharmaciens experts" to="/catalogue?featured=1" />
            <Grid products={featured} />
          </section>
        )}

        {/* Brands */}
        {brands.length > 0 && (
          <section data-testid="section-marques">
            <SectionHeader title="Nos Marques de Référence" subtitle="Les plus grands laboratoires disponibles en Algérie" to="/marques" />
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 sm:gap-4">
              {brands.slice(0, 10).map((b) => (
                <Link key={b.id} to={`/marque/${b.id}`} data-testid={`home-brand-${b.id}`}
                  className="aspect-[3/2] rounded-2xl bg-white border border-slate-200/80 hover:border-mint-400 hover:shadow-md grid place-items-center p-4 transition-all">
                  {b.logo ? <img src={mediaUrl(b.logo)} alt={b.name} className="max-h-full max-w-full object-contain" /> :
                    <span className="font-display font-bold text-slate-700 text-center text-sm sm:text-base">{b.name}</span>}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Blog */}
        {blog.length > 0 && (
          <section data-testid="section-blog">
            <SectionHeader title="Conseils & Astuces" subtitle="Guides rédigés par nos spécialistes pour prendre soin de vous" to="/blog" />
            <div className="grid sm:grid-cols-3 gap-5">
              {blog.map((post) => (
                <Link key={post.id} to={`/blog/${post.id}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-200/80 hover:shadow-xl transition-all">
                  <div className="aspect-[16/10] overflow-hidden bg-mint-50">
                    <img src={mediaUrl(post.image)} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <div className="text-xs text-mint-600 font-mono-label mb-2">{post.author}</div>
                    <h3 className="font-display font-bold text-slate-dark leading-snug line-clamp-2 group-hover:text-mint-700 transition-colors">{post.title}</h3>
                    <p className="text-sm text-slate-500 mt-2 line-clamp-2">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
